import React from "react";
import { useNavigate } from "react-router-dom";
import { Lifebuoy, WarningCircle, CaretRight, ChatCircleDots, Factory } from "@phosphor-icons/react";
import { ago } from "@/components/admin/tickets/TicketCard";
import { Panel } from "./dashUtils";

function Stat({ label, value, cls, testid }) {
  return (
    <div className={`rounded-lg border p-3.5 ${cls}`} data-testid={testid}>
      <div className="font-heading text-2xl font-bold leading-none">{value}</div>
      <div className="text-[0.65rem] uppercase tracking-wider mt-1.5 opacity-80">{label}</div>
    </div>
  );
}

const viewAll = (onClick, text, testid) => (
  <button onClick={onClick} data-testid={testid} className="text-xs text-[#73bf44] hover:underline inline-flex items-center gap-0.5">{text} <CaretRight size={11} /></button>
);

export function TicketsOverview({ data }) {
  const navigate = useNavigate();
  const t = data?.tickets;
  return (
    <Panel title="Support tickets" testid="tickets-overview-card" aside={viewAll(() => navigate("/admin/support-tickets"), "Manage tickets", "tickets-overview-link")}>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Stat label="In progress" value={t?.in_progress ?? "—"} cls="bg-amber-500/10 border-amber-500/20 text-amber-400" testid="tickets-in-progress-count" />
        <Stat label="Completed" value={t?.completed ?? "—"} cls="bg-[#73bf44]/10 border-[#73bf44]/20 text-[#73bf44]" testid="tickets-completed-count" />
      </div>
      <div className="space-y-2">
        {t?.recent?.length ? t.recent.map((x) => (
          <button key={x.id} onClick={() => navigate(`/admin/support-tickets?q=${x.reference}`)} data-testid="tickets-overview-item"
            className="w-full text-left bg-[#171717] hover:bg-[#1f1f1f] border border-neutral-800 p-3 rounded-lg transition-colors flex items-start gap-3">
            <Lifebuoy size={16} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono font-bold text-white">{x.reference}</span>
                <span className="text-neutral-400 truncate">{x.name} · <span className="font-mono text-[#73bf44]">{x.chassis_number}</span></span>
                <span className="ml-auto text-neutral-600 shrink-0">{ago(x.created_at)}</span>
              </div>
              <div className="text-xs text-neutral-500 truncate mt-0.5">{x.summary}</div>
            </div>
          </button>
        )) : <p className="text-sm text-neutral-600 py-2" data-testid="tickets-overview-empty">No tickets waiting.</p>}
      </div>
    </Panel>
  );
}

export function DiscrepanciesOverview({ data }) {
  const navigate = useNavigate();
  const d = data?.discrepancies;
  return (
    <Panel title="Config discrepancies" testid="discrepancies-overview-card" aside={viewAll(() => navigate("/admin/discrepancies"), "Review queue", "discrepancies-overview-link")}>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat label="Open" value={d?.open ?? "—"} cls="bg-red-500/10 border-red-500/20 text-red-400" testid="disc-open-count" />
        <Stat label="Resolved" value={d?.resolved ?? "—"} cls="bg-emerald-500/10 border-emerald-500/20 text-emerald-400" testid="disc-resolved-count" />
        <Stat label="Dismissed" value={d?.dismissed ?? "—"} cls="bg-neutral-800/40 border-neutral-800 text-neutral-400" testid="disc-dismissed-count" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#73bf44]/30 bg-[#73bf44]/10 text-[#73bf44] px-2.5 py-1" data-testid="disc-open-customer"><ChatCircleDots size={12} /> {d?.open_customer ?? 0} from customers</span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 px-2.5 py-1" data-testid="disc-open-sync"><Factory size={12} /> {d?.open_sync ?? 0} from production sync</span>
      </div>
      <div className="space-y-2">
        {d?.recent?.length ? d.recent.map((x) => (
          <button key={x.id} onClick={() => navigate("/admin/discrepancies")} data-testid="disc-overview-item"
            className="w-full text-left bg-[#171717] hover:bg-[#1f1f1f] border border-neutral-800 p-3 rounded-lg transition-colors flex items-start gap-3">
            <WarningCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono font-bold text-[#73bf44]">{x.chassis_number}</span>
                <span className="text-neutral-400 truncate">{x.model}</span>
                <span className="ml-auto text-neutral-600 shrink-0">{ago(x.created_at)}</span>
              </div>
              <div className="text-xs text-neutral-500 truncate mt-0.5">{x.fields?.length ? x.fields.join(", ") : x.note || "Details to review"}</div>
            </div>
          </button>
        )) : <p className="text-sm text-neutral-600 py-2">Nothing waiting for review.</p>}
      </div>
    </Panel>
  );
}
